<template>
  <div class="group-detail">
    <h1 class="common__main-title">Application/Student Placement Irregularity Report</h1>
    <div class="common__sub-title">
      Details
      <span class="common__sub-title-tag">Register Now</span>
    </div>
    <div class="content-wrapper">
      <div class="main-content">

        <form :class="{ 'confirm-contents': isConfirmed }">
          <template v-if="!isConfirmed">
            <!-- 入力画面 -->
            <div class="form-group">
              <label for="targetTutor" class="form-label">Tutor Name</label>
              <template v-if="loginedUser.authority === 'tutor'">
                <div class="col-sm-10">
                  {{ targetTutor.name }} {{ targetTutor.position.name }}
                </div>
              </template>
              <template v-else>
                <div class="col-sm-8">
                  <SearchTutor v-model="targetTutor" />
                  <div class="text-danger" v-show="!targetTutor">Please select tutor</div>
                </div>
              </template>
            </div>

            <div class="form-group">
              <label class="form-label">Application Type</label>
              <div class="form-check col-sm-10">
                <div class="form-check form-check-inline" v-for="type of types" :key="type.code">
                  <label class="form-check-label">
                    <input type="radio" name="type" :value="type.code"
                      v-model="applicationType" :disabled="isConfirmed">
                    {{ type.name }}
                  </label>
                </div>
              </div>
            </div>
          </template>

          <template v-else>
            <!-- 確認画面 -->
            <div class="form-group">
              <label for="targetTutor" class="form-label">Tutor Name</label>
              <div class="col-sm-10 form-confirm-value">
                {{ targetTutor.name }} {{ targetTutor.position.name }}
              </div>
            </div>

            <div class="form-group">
              <label class="form-label">Application Type</label>
              <div class="col-sm-10 form-confirm-value">
                {{ applicationType | applicationName }}
              </div>
            </div>
          </template>

          <hr />

          <component
            :is="applicationType"
            :ref="applicationType"
            :targetTutor="targetTutor"
            :isConfirmed="isConfirmed"
            status="unapproved"
          />
        </form>

      </div>

      <div class="d-flex justify-content-between">
        <template v-if="!isConfirmed">
          <b-button pill variant="secondary" :to="{ name: 'Applications' }">Back</b-button>
          <b-button pill variant="primary" @click="confirm">Confirm</b-button>
        </template>
        <template v-else>
          <b-button pill variant="secondary" @click="isConfirmed = false">Back</b-button>
          <b-button pill variant="primary" @click="register">Register</b-button>
          <b-modal id="apprpvedErrorModal">
            Application cannot be registered because the previous inquiry has not been approved.
          </b-modal>
        </template>
      </div>

      <SuccessModal ref="successModal" @ok="$router.push({ name:'Applications' })" />
      <ErrorModal ref="errorModal" />
      <Loading :isLoading="isLoading" />

    </div>
  </div>
</template>

<script lang="ts">
import Vue from 'vue'
import _ from 'lodash'
import Vacation from '@/components/parts/application/types/Vacation.vue'
import Booth from '@/components/parts/application/types/Booth.vue'
import WorkOvertime from '@/components/parts/application/types/WorkOvertime.vue'
import Spir from '@/components/parts/application/types/Spir.vue'
import Inquiry from '@/components/parts/application/types/EvaluationInquiry.vue'
import SearchTutor from '@/components/parts/inputItem/SearchTutor.vue'
import SuccessModal from '@/components/parts/modal/Success.vue'
import ErrorModal from '@/components/parts/modal/Error.vue'
import { Tutor } from '@/store/tutors/types'
import { applicationTypeCode, ApplicationType } from '@/store/applications/types'
import Loading from '@/components/parts/modal/Loading.vue'

/**
 * ApplicationType用のVueインタフェース
 */
export interface IAddApplicationComponent extends Vue {
  /**
   * バリデーション
   * @return {boolean} true: 問題なし, false: バリデーションエラー
   */
  validation(): Promise<boolean>
  /**
   * 登録処理
   * @param {Tutor} targetTutor 申請対象チューター
   */
  register(targetTutor: Tutor): Promise<void>
}

export default Vue.extend({
  components: {
    SearchTutor,
    Vacation,
    Booth,
    WorkOvertime,
    Spir,
    Inquiry,
    SuccessModal,
    ErrorModal,
    Loading,
  },
  data: (): {
    types: { code: string, name: string }[],
    targetTutor: Tutor|null,
    applicationType: applicationTypeCode,
    isConfirmed: boolean,
    isLoading: boolean,
  } => ({
    types: [
      { code: 'vacation', name: 'Absence Day' },
      { code: 'booth', name: 'Booth' },
      { code: 'workOvertime', name: 'Work on day with no classes' },
      { code: 'spir', name: 'Student Placement Irregularity Report' },
      { code: 'inquiry', name: 'Inquiry' },
    ],
    targetTutor: null,
    applicationType: 'vacation',
    isConfirmed: false,
    isLoading: false,
  }),

  computed: {
    loginedUser(): Tutor {
      return this.$store.state.auth.profile
    },
  },
  created(): void {
    if (this.loginedUser.authority === 'tutor') {
      // 権限がチューターの場合、自身の申請のみ
      this.targetTutor = this.loginedUser
    }
  },
  methods: {
    confirm() {
      if (!this.targetTutor) {
        return
      }

      if (
        this.$refs[this.applicationType] && (this.$refs[this.applicationType] as any).lesson
        && !(this.$refs[this.applicationType] as any).lesson.isApproved) {
        return
      }

      (this.$refs[this.applicationType] as IAddApplicationComponent)
        .validation()
        .then((validationResult: boolean) => {
          this.isConfirmed = validationResult
        })
    },
    register() {
      if (this.isLoading) return
      if (
        this.$refs[this.applicationType] && (this.$refs[this.applicationType] as any).lesson
        && !(this.$refs[this.applicationType] as any).lesson.isApproved
        ) {
        this.$bvModal.show('apprpvedErrorModal')
        return
      }
      this.isLoading = true;
      (this.$refs[this.applicationType] as IAddApplicationComponent)
        .register(this.targetTutor as Tutor)
        .then(() => (this.$refs.successModal as SuccessModal).open())
        .catch((error) => {
          const message = _.has(error, 'response.data.message') ? error.response.data.message : error.message;
          (this.$refs.errorModal as ErrorModal).open(message)
        })
        .finally(() => { this.isLoading = false })
    },
  },
  filters: {
    applicationName: (t: applicationTypeCode): string => ApplicationType[t],
  },
})
</script>

<style lang="scss">
.content-wrapper {
  background: #D8D8D8;
  padding: 20px 50px;
  min-height: 650px;
}

.main-content {
  padding: 20px;
  border-radius: 20px;
  margin-bottom:20px;
  min-height: 500px;
}

.confirm-contents {
  background: #fff;
  padding: 20px;
  border-radius: 20px;
  margin-bottom:20px;

  .form-label, .detail-item-title {
    font-weight: bold;
    text-align: left;
    margin:10px 0;
  }

  .form-confirm-value, .detail-item {
    height:20px;
    margin-bottom: 20px;
    margin-left: 10px;
    font-size: 16px;
  }
}

.form-group {
  .form-label {
    font-weight: bold;
    text-align: left;
    margin:10px 0;
  }

  .form-confirm-value {
    height:20px;
    margin-bottom: 20px;
    margin-left: 10px;
    font-size: 16px;
  }
}

</style>
